import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { IModalRefProps } from 'ui/components/Modals/Modal/types';
import { ITabsRefProps } from 'ui/components/Tabs/types';
import { ActivityCategory } from 'ui/types/activities';
import { IEcosystem } from 'ui/types/interfaces/ecosystem';
import { MeasurementUnit } from 'ui/types/measurementUnit';

import { PAGES } from '~/constants/pages.constants';
import { EcosystemVisibility } from '~/enums';
import { useLoadOrganizationCauseOptions } from '~/hooks/useLoadOrganizationCauseOptions';
import { useLoadMeasurementUnits } from '~/hooks/useMeasurementUnit';
import { useQuery } from '~/hooks/useQuery';
import { useRouter } from '~/hooks/useRouter';
import {
  groupMeasurementByCategory,
  mapGroupedMeasurementToOptions,
} from '~/utils/activitymetrics';

import { defaultValues, schemaValidation } from './constants';
import { ICreateEcosystemForm } from './types';

export const useEcosystemFormController = (
  onSubmit: (values: ICreateEcosystemForm) => void,
  ecosystemIdToEdit?: string,
) => {
  const tabsRef = useRef<ITabsRefProps>(null);
  const modalRef = useRef<IModalRefProps>(null);
  const { replaceRoute, goBack } = useRouter();

  const { data: ecosystem, isLoading: isLoadingEcosystem } =
    useQuery<IEcosystem>(`/ecosystem/${ecosystemIdToEdit}`, {
      queryOptions: { enabled: !!ecosystemIdToEdit },
    });

  const { causeOptionsData, isLoading: isLoadingCauses } =
    useLoadOrganizationCauseOptions();

  const { data: measurementUnitsData, isLoading: isLoadingMeasurementUnits } =
    useLoadMeasurementUnits();

  const {
    control,
    formState: { isSubmitting, isValid, errors },
    reset,
    getValues,
    watch,
    setValue,
    handleSubmit,
  } = useForm<ICreateEcosystemForm>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(schemaValidation),
  });
  const currentTab = watch('currentStep');
  const isPrivate = watch('isPrivate');

  const handleContinue = () => {
    const totalSteps = ecosystemIdToEdit ? 1 : 2;
    const currentStep = getValues('currentStep');
    if (currentStep < totalSteps) {
      setValue('currentStep', currentStep + 1, { shouldValidate: true });
      tabsRef.current?.nextStep();
    }
  };

  const handleBack = () => {
    const currentStep = getValues('currentStep');
    if (currentStep >= 1) {
      setValue('currentStep', currentStep - 1, { shouldValidate: true });
      tabsRef.current?.backStep();
      return;
    }
    goBack();
  };

  useEffect(() => {
    if (!ecosystemIdToEdit || isLoadingEcosystem || !ecosystem) return;

    const causeOptions = causeOptionsData?.data
      ?.filter((option: ActivityCategory) =>
        ecosystem.causeOptions?.includes(option._id || ''),
      )
      .map((c) => ({
        value: c._id || '',
        label: c.displayName || '',
      }));

    const measurementUnits = measurementUnitsData?.data
      ?.filter((unit: MeasurementUnit) =>
        ecosystem.measurementUnits?.find((u) => u._id === unit._id),
      )
      .map((u) => ({
        value: u._id || '',
        label: u.singularLabel || '',
      }));

    reset({
      ...ecosystem,
      isPrivate: ecosystem.visibility === EcosystemVisibility.Private,
      logo: ecosystem.logo,
      currentStep: 0,
      causeOptions,
      measurementUnits,
    });
  }, [
    ecosystemIdToEdit,
    causeOptionsData,
    ecosystem,
    isLoadingEcosystem,
    measurementUnitsData,
    reset,
    setValue,
  ]);

  useEffect(() => {
    if (ecosystemIdToEdit && !isLoadingEcosystem && !ecosystem) {
      toast.error('Ecosystem not found.');
      replaceRoute(PAGES.ROOT);
    }
  }, [ecosystemIdToEdit, ecosystem, isLoadingEcosystem, replaceRoute]);

  const causeOptions = useMemo(() => {
    return (
      causeOptionsData?.data?.map((option: ActivityCategory) => ({
        value: option._id || '',
        label: option.displayName || '',
      })) || []
    );
  }, [causeOptionsData?.data]);

  const measurementUnitOptions = useMemo(() => {
    if (!measurementUnitsData?.data) return [];
    const groupedByCategory = groupMeasurementByCategory(
      measurementUnitsData.data,
    );
    return mapGroupedMeasurementToOptions(groupedByCategory);
  }, [measurementUnitsData?.data]);

  const isLoading =
    isLoadingEcosystem || isLoadingCauses || isLoadingMeasurementUnits;

  return {
    control,
    errors,
    isValid,
    tabsRef,
    currentTab,
    isSubmitting,
    isPrivate,
    modalRef,
    isLoading,
    causeOptions,
    measurementUnitOptions,
    handleBack,
    handleContinue,
    handleOnSubmit: handleSubmit(onSubmit),
  };
};
